import { useCallback, useEffect, useMemo, useState } from 'react'
import { play, playSteps, stop } from '@/audio/player'
import { binnenTimeline } from '@/core/binnenSignale'
import { cardFace, nextCard, recordCard } from '@/core/cards'
import { binnenSignale, signals } from '@/core/data'
import { binnenSignalPrompt } from '@/core/quiz'
import { loadProgress, saveProgress, type Progress, naechsteAusKorb, type KorbId } from '@/core/progress'
import { useSettings } from './settings'
import { subjectOfCard, type Subject } from './Viewer'

export interface CardsProps {
  /** Der Korb, aus dem geübt wird; die App hält ihn über der Szene. */
  korb?: KorbId
  /** Zählt hoch, wenn Bereich oder Korb gewechselt haben. */
  korbWechsel?: number
  onSubject: (subject: Subject | undefined) => void
}

/**
 * Karteikarten: erst das Bild, dann die Antwort.
 *
 * Wer vier Vorschläge sieht, erkennt die richtige oft wieder, ohne sie zu wissen. Hier
 * steht nur das Bild, und aufgedeckt wird, wenn man es selbst will.
 */
export function Cards({ onSubject, korb, korbWechsel = 0 }: CardsProps) {
  const { t, lang, settings } = useSettings()
  const bereich = settings.bereich
  const [progress, setProgress] = useState<Progress>(() => loadProgress())
  const [cardId, setCardId] = useState<string | undefined>(() => nextCard(loadProgress(), undefined, bereich))
  const [open, setOpen] = useState(false)

  /** Wechselt Bereich oder Korb, zieht die Karte aus dem neuen Stapel. */
  useEffect(() => {
    if (korbWechsel === 0) return
    setOpen(false)
    const naechste = korb
      ? naechsteAusKorb(loadProgress(), korb, bereich)
      : nextCard(loadProgress(), undefined, bereich)
    if (naechste) setCardId(naechste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [korbWechsel])

  const face = useMemo(() => (cardId ? cardFace(cardId, lang) : undefined), [cardId, lang])

  useEffect(() => {
    onSubject(face ? subjectOfCard(face.cardId) : undefined)
    return () => stop()
  }, [face, onSubject])

  const answer = useCallback(
    (known: boolean) => {
      if (!cardId) return
      stop()
      const next = recordCard(progress, cardId, known)
      setProgress(next)
      saveProgress(next)
      setOpen(false)
      setCardId(nextCard(next, undefined, bereich))
    },
    [cardId, progress],
  )

  if (!face) return <p className="detail__text">{t('quiz.empty')}</p>

  const signal = face.kind === 'signal' ? signals.find((s) => s.id === face.subject) : undefined
  const binnenZeichen =
    face.kind === 'binnenSignal' ? binnenSignale.find((z) => z.id === face.subject) : undefined
  const istZeichen = face.kind === 'signal' || face.kind === 'binnenSignal'

  return (
    <section className="cards" data-testid="cards" data-bereich={bereich}>
      <h2 className="quiz__prompt">{t(istZeichen ? 'cards.prompt.signal' : 'cards.prompt')}</h2>

      {signal && (
        <div className="signal__controls">
          <button
            type="button"
            className="signal__play"
            data-testid="cards-play"
            onClick={() => void play(signal)}
          >
            {t('quiz.signal.play')}
          </button>
        </div>
      )}
      {/* Das Binnenzeichen nennt seine Lage mit, denn derselbe Ton heißt auf dem Rhein je
          nach Lage Verschiedenes, und ohne sie wäre die Karte nicht zu beantworten. */}
      {binnenZeichen && (
        <div className="signal__controls" data-testid="cards-binnen-signal">
          <p className="detail__text" lang="de">
            {binnenSignalPrompt(binnenZeichen)[lang]}
          </p>
          {binnenZeichen.pattern.length > 0 && (
            <button
              type="button"
              className="signal__play"
              data-testid="cards-play"
              onClick={() => void playSteps(binnenTimeline(binnenZeichen))}
            >
              {t('quiz.signal.play')}
            </button>
          )}
        </div>
      )}

      {!open ? (
        <button type="button" className="signal__play" data-testid="cards-reveal" onClick={() => setOpen(true)}>
          {t('cards.reveal')}
        </button>
      ) : (
        <div className="quiz__result" data-testid="cards-face">
          <h3 data-testid="cards-title">{face.title}</h3>
          <p className="detail__text">{face.explanation}</p>
          {face.character && (
            <p className="detail__character" data-testid="cards-character">
              {face.character}
            </p>
          )}
          <h3 className="detail__legend" data-testid="cards-citation">
            {face.citation}
          </h3>
          <ul className="detail__quotes">
            {face.quotes.slice(0, 3).map((quote) => (
              <li key={quote.slice(0, 40)}>{quote}</li>
            ))}
          </ul>
          <div className="signal__controls">
            <button type="button" className="signal__play" data-testid="cards-known" onClick={() => answer(true)}>
              {t('cards.known')}
            </button>
            <button type="button" className="toggle" data-testid="cards-unknown" onClick={() => answer(false)}>
              {t('cards.unknown')}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
